// hooks/useConnectingRetry.js — EL SUBMIT QUE PELEA SOLO (F6.7b, pasada final 2).
//
// El problema medido (smoke 28-jul): con el wifi de vuelta, el POST de login
// siguió fallando con forma-de-red ~2 minutos — la ventana post-NET.UP en que
// el transporte de iOS todavía no sirve. El aviso de offline se quitaba bien
// (el vigía dictaminó online), pero el formulario le entregaba al usuario un
// error por un fallo que se arregla solo con esperar y reintentar.
//
// Este hook es la escalera de ese pleito: reintenta el submit a 1 s → 2 s →
// 4 s → y luego cada 5 s, mientras el usuario permanezca en la pantalla, con
// un estado visible "Conectando…" y un botón Cancelar que corta y regresa al
// formulario. Reglas duras:
//   - UN solo pleito vivo (token): un start nuevo o un cancel invalida al
//     anterior aunque su intento siga en vuelo.
//   - Desmontar cancela: nada pelea desde una pantalla que ya no existe.
//   - El hook NO clasifica errores: attempt() decide 'retry' (siguiente
//     peldaño) o cualquier otra cosa (fin — el llamador ya pintó su UI). La
//     regla "jamás reintentar credenciales malas" vive en el caller, que es
//     quien sabe qué significa cada fallo de SU endpoint.
//
// La condición de entrada (forma-de-red Y veredicto del vigía ≠ offline)
// también es del caller. En web el veredicto es siempre UNKNOWN (vigía
// app-only), así que allá el pleito también aplica a fallos sin respuesta —
// coherente con la regla: solo el dictamen explícito de offline lo detiene.

import { useCallback, useEffect, useRef, useState } from 'react';

// Peldaños del pleito: 1 s → 2 s → 4 s, luego CADA 5 s (constante, no
// crecimiento infinito: el usuario está mirando la pantalla y 5 s sostiene la
// promesa de "entro solo en cuanto se pueda" sin martillar al servidor).
export const CONNECT_RETRY_LADDER_MS = [1000, 2000, 4000];
export const CONNECT_RETRY_STEADY_MS = 5000;

export function useConnectingRetry() {
  const [connecting, setConnecting] = useState(false);
  const tokenRef = useRef(0);
  const timerRef = useRef(null);

  const cancel = useCallback(() => {
    tokenRef.current += 1;
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setConnecting(false);
  }, []);

  // Desmontar corta el pleito (cleanup del effect = el cancel de despedida).
  useEffect(() => cancel, [cancel]);

  /**
   * start(attempt) — entra al estado "Conectando…" y programa el primer
   * peldaño. attempt() → Promise<'retry'|any>: 'retry' sube el siguiente
   * peldaño; cualquier otro valor termina el estado (el caller ya manejó el
   * éxito o pintó el error). Un start durante un pleito vivo lo reemplaza.
   */
  const start = useCallback((attempt) => {
    const token = ++tokenRef.current;
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setConnecting(true);
    const schedule = (step) => {
      const delay = step < CONNECT_RETRY_LADDER_MS.length
        ? CONNECT_RETRY_LADDER_MS[step]
        : CONNECT_RETRY_STEADY_MS;
      timerRef.current = setTimeout(async () => {
        timerRef.current = null;
        let outcome = null;
        try {
          outcome = await attempt();
        } catch (e) {
          // attempt() clasifica adentro y no debería lanzar; si lanza, el
          // pleito termina — jamás un bucle sobre un error desconocido.
          outcome = null;
        }
        if (token !== tokenRef.current) return; // cancelado/reemplazado en vuelo
        if (outcome === 'retry') schedule(step + 1);
        else setConnecting(false);
      }, delay);
    };
    schedule(0);
  }, []);

  return { connecting, start, cancel };
}

export default useConnectingRetry;
